import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Switch,
  Box,
  Alert,
  Autocomplete,
  CircularProgress,
  Snackbar,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { ptBR } from "date-fns/locale";
import { ISchedule, ScheduleStatus, IPetPopulated } from "../types";
import {
  createSchedule,
  updateSchedule,
  getPets,
  getServices,
} from "../services";

interface ModalAgendamentoProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  schedule?: ISchedule | null;
}

interface Service {
  ID: string;
  Nome: string;
  Descricao?: string;
}

const ModalAgendamento: React.FC<ModalAgendamentoProps> = ({
  open,
  onClose,
  onSave,
  schedule,
}) => {
  const [formData, setFormData] = useState({
    petId: "",
    serviceName: "",
    serviceExternalId: "",
    scheduledAt: new Date(),
    status: ScheduleStatus.PENDENTE,
    requiresPickup: false,
    isRecurring: false,
    recurrencePattern: "",
    notes: "",
  });

  const [shouldCreateNotification, setShouldCreateNotification] =
    useState(true); // Padrão: criar notificação
  const [pets, setPets] = useState<IPetPopulated[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [loadingPets, setLoadingPets] = useState(false);
  const [loadingServices, setLoadingServices] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);

  // Carregar pets
  useEffect(() => {
    const loadPets = async () => {
      setLoadingPets(true);
      try {
        const data = await getPets();
        setPets(data || []);
      } catch (err) {
        setError("Erro ao carregar lista de pets");
      } finally {
        setLoadingPets(false);
      }
    };

    if (open) {
      loadPets();
    }
  }, [open]);

  // Carregar serviços
  useEffect(() => {
    const loadServices = async () => {
      setLoadingServices(true);
      try {
        const data = await getServices();
        setServices(data || []);
      } catch (err) {
        setError("Erro ao carregar lista de serviços");
      } finally {
        setLoadingServices(false);
      }
    };

    if (open) {
      loadServices();
    }
  }, [open]);

  // Preencher formulário quando editando
  useEffect(() => {
    if (schedule) {
      setFormData({
        petId: schedule.petId,
        serviceName: schedule.serviceName,
        serviceExternalId: schedule.serviceExternalId || "",
        scheduledAt: new Date(schedule.scheduledAt),
        status: schedule.status,
        requiresPickup: schedule.requiresPickup,
        isRecurring: schedule.isRecurring,
        recurrencePattern: schedule.recurrencePattern || "",
        notes: schedule.notes || "",
      });
    } else {
      // Reset form
      setFormData({
        petId: "",
        serviceName: "",
        serviceExternalId: "",
        scheduledAt: new Date(),
        status: ScheduleStatus.PENDENTE,
        requiresPickup: false,
        isRecurring: false,
        recurrencePattern: "",
        notes: "",
      });
    }
    setError(null);
  }, [schedule, open]);

  const handleSubmit = async () => {
    // Validação
    if (!formData.petId || !formData.serviceName || !formData.scheduledAt) {
      setError("Por favor, preencha todos os campos obrigatórios");
      return;
    }

    // Validação adicional para recorrência
    if (formData.isRecurring && !formData.recurrencePattern) {
      setError(
        "Por favor, selecione a frequência para agendamentos recorrentes"
      );
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const dataToSubmit = {
        ...formData,
        createNotification: shouldCreateNotification, // Incluir opção de notificação
      };

      if (schedule) {
        // Atualizar
        await updateSchedule(schedule.id, dataToSubmit);
        onSave();
      } else {
        // Criar
        const response = await createSchedule(dataToSubmit);

        // Se foram criados múltiplos agendamentos recorrentes, exibir mensagem
        if (
          response.totalRecurringSchedules &&
          response.totalRecurringSchedules > 1
        ) {
          setSuccessMessage(
            `${response.totalRecurringSchedules} agendamentos foram criados para os próximos 60 dias!`
          );
          setShowSuccessSnackbar(true);

          // Aguardar 3 segundos antes de fechar o modal
          setTimeout(() => {
            onSave();
          }, 3000);
        } else {
          // Se não for recorrente, fechar imediatamente
          onSave();
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.message || "Erro ao salvar agendamento");
    } finally {
      setSubmitting(false);
    }
  };

  const handleServiceChange = (service: Service | null) => {
    if (service) {
      setFormData({
        ...formData,
        serviceName: service.Nome,
        serviceExternalId: service.ID,
      });
    } else {
      setFormData({
        ...formData,
        serviceName: "",
        serviceExternalId: "",
      });
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
      {/* Snackbar de Sucesso - Fora do Dialog para garantir visibilidade */}
      <Snackbar
        open={showSuccessSnackbar}
        autoHideDuration={3000}
        onClose={() => setShowSuccessSnackbar(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{ zIndex: 9999 }}
      >
        <Alert
          onClose={() => setShowSuccessSnackbar(false)}
          severity="success"
          variant="filled"
          sx={{ width: "100%", fontSize: "1rem" }}
        >
          {successMessage}
        </Alert>
      </Snackbar>

      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {schedule ? "Editar Agendamento" : "Novo Agendamento"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
            {error && <Alert severity="error">{error}</Alert>}

            {/* Seleção de Pet */}
            <FormControl fullWidth required>
              <Autocomplete
                options={pets}
                getOptionLabel={(option) =>
                  `${option.name} - ${option.tutor?.name || "Sem tutor"}`
                }
                value={pets.find((p) => p.id === formData.petId) || null}
                onChange={(_, value) =>
                  setFormData({ ...formData, petId: value?.id || "" })
                }
                loading={loadingPets}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Pet *"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingPets ? <CircularProgress size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </FormControl>

            {/* Seleção de Serviço */}
            <FormControl fullWidth required>
              <Autocomplete
                options={services}
                getOptionLabel={(option) =>
                  typeof option === "string" ? option : option.Nome
                }
                value={
                  services.find((s) => s.ID === formData.serviceExternalId) ||
                  null
                }
                onChange={(_, value) => {
                  if (typeof value === "string") {
                    setFormData({
                      ...formData,
                      serviceName: value,
                      serviceExternalId: "",
                    });
                  } else {
                    handleServiceChange(value);
                  }
                }}
                loading={loadingServices}
                freeSolo
                onInputChange={(_, value) => {
                  if (value && !services.find((s) => s.Nome === value)) {
                    setFormData({ ...formData, serviceName: value });
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Serviço *"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingServices ? (
                            <CircularProgress size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </FormControl>

            {/* Data e Hora */}
            <DateTimePicker
              label="Data e Hora *"
              value={formData.scheduledAt}
              onChange={(newValue) =>
                setFormData({
                  ...formData,
                  scheduledAt: newValue || new Date(),
                })
              }
              slotProps={{
                textField: {
                  fullWidth: true,
                  required: true,
                },
              }}
            />

            {/* Status */}
            {schedule && (
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  label="Status"
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      status: e.target.value as ScheduleStatus,
                    })
                  }
                >
                  <MenuItem value={ScheduleStatus.PENDENTE}>Pendente</MenuItem>
                  <MenuItem value={ScheduleStatus.CONFIRMADO}>
                    Confirmado
                  </MenuItem>
                  <MenuItem value={ScheduleStatus.EM_ANDAMENTO}>
                    Em Andamento
                  </MenuItem>
                  <MenuItem value={ScheduleStatus.CONCLUIDO}>
                    Concluído
                  </MenuItem>
                  <MenuItem value={ScheduleStatus.CANCELADO}>
                    Cancelado
                  </MenuItem>
                </Select>
              </FormControl>
            )}

            {/* Checkbox de Busca */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.requiresPickup}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      requiresPickup: e.target.checked,
                    })
                  }
                />
              }
              label="Requer busca do pet"
            />

            {/* Checkbox de Recorrência */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.isRecurring}
                  onChange={(e) =>
                    setFormData({ ...formData, isRecurring: e.target.checked })
                  }
                />
              }
              label="Agendamento recorrente"
            />

            {/* Informação sobre recorrência */}
            {formData.isRecurring && (
              <Alert severity="info" sx={{ mt: 1 }}>
                Ao criar um agendamento recorrente, serão criados
                automaticamente múltiplos agendamentos para os próximos 60 dias,
                de acordo com a frequência selecionada.
              </Alert>
            )}

            {/* Switch para criar notificação */}
            <FormControlLabel
              control={
                <Switch
                  checked={shouldCreateNotification}
                  onChange={(e) =>
                    setShouldCreateNotification(e.target.checked)
                  }
                  color="primary"
                />
              }
              label="Criar notificação para este agendamento"
            />

            {/* Padrão de Recorrência */}
            {formData.isRecurring && (
              <FormControl fullWidth>
                <InputLabel>Frequência</InputLabel>
                <Select
                  value={formData.recurrencePattern}
                  label="Frequência"
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      recurrencePattern: e.target.value,
                    })
                  }
                >
                  <MenuItem value="SEMANAL">Semanal</MenuItem>
                  <MenuItem value="QUINZENAL">Quinzenal</MenuItem>
                  <MenuItem value="MENSAL">Mensal</MenuItem>
                  <MenuItem value="BIMESTRAL">Bimestral</MenuItem>
                  <MenuItem value="TRIMESTRAL">Trimestral</MenuItem>
                </Select>
              </FormControl>
            )}

            {/* Observações */}
            <TextField
              label="Observações"
              multiline
              rows={3}
              fullWidth
              value={formData.notes}
              onChange={(e) =>
                setFormData({ ...formData, notes: e.target.value })
              }
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={submitting}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={submitting}
          >
            {submitting ? <CircularProgress size={24} /> : "Salvar"}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default ModalAgendamento;
